import clsx from 'clsx';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { selectIsAdmin, selectAuthTenantId } from '../../features/auth/store/authSlice';
import { TemplatePreviewPanel } from '../../features/templates/ui';
import { PageSubheader } from '../../shared/ui/page-subheader/PageSubheader';
import { StickyPanel } from '../../shared/ui/sticky-panel/StickyPanel';
import { ChevronLeftIcon } from '../../shared/ui/icons';
import { IconButton } from '../../shared/ui/icon-button/IconButton';
import { SpinnerWrap } from '../../shared/ui/spinner';
import styles from './EmailTemplateCreatePage.module.css';
import layoutStyles from '../../shared/styles/layout.module.css';
import scrollbarStyles from '../../shared/styles/scrollbarThin.module.css';
import { useApiReady, usePageTitle } from '../../shared/hooks';
import {
  BasicInformationCard,
  ContentEditorCard,
  DiscardChangesDialog,
  DisclosureCard,
  VariablesCard,
} from '../../features/templates/ui/create-template';
import { useTemplateForm } from '../../features/templates/hooks/useTemplateForm';
import { useCreateTemplateMetaLoader } from '../../features/templates/hooks/useCreateTemplateMetaLoader';
import { useTemplateSourceLoader } from '../../features/templates/hooks/useTemplateSourceLoader';
import { CreateTemplateActions } from './ui/CreateTemplateActions';
import { getCreateTemplatePageConfig } from './lib/getCreateTemplatePageConfig';
import { buildCreatePreviewTemplate } from './lib/buildCreatePreviewTemplate';
import { CHANNEL } from '../../features/templates/constants/messageTemplateKeys';

export function EmailTemplateCreatePage() {
  const location = useLocation();
  const { templateId } = useParams();
  const apiReady = useApiReady();

  const [editorContent, setEditorContent] = useState('');
  const [checkedDisclosures, setCheckedDisclosures] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [isEditorReady, setIsEditorReady] = useState(false);

  const { channelKey, isEditMode, pageTitle } = getCreateTemplatePageConfig(
    location.pathname || '',
  );
  usePageTitle(pageTitle);
  // Copy has no route of its own: the Templates list passes the source template id in navigation
  // state, so the URL is the plain Create one and only the form is seeded from that template.
  const copyFromTemplateId = isEditMode ? null : (location.state?.copyFromTemplateId ?? null);
  const isCopyMode = String(copyFromTemplateId ?? '').trim().length > 0;
  const sourceTemplateId = isEditMode ? templateId : copyFromTemplateId;
  const hasSourceTemplateId = String(sourceTemplateId ?? '').trim().length > 0;
  const showDisclosure = channelKey === CHANNEL.EMAIL;
  const isAdmin = useSelector(selectIsAdmin);
  const ownTenantId = useSelector(selectAuthTenantId);

  const { disclosures, tenantLogoUrl, tenantSender, isMetaLoading, variableChannel } =
    useCreateTemplateMetaLoader({
      channel: channelKey,
    });

  const {
    isSourceDataLoading,
    initialBasicInfoForm,
    initialEditorContent,
    initialCustomStyle,
    initialCheckedDisclosureIds,
    currentStatus,
    modified,
    lastEditedBy,
  } = useTemplateSourceLoader({
    isEditMode,
    isCopyMode,
    hasTemplateId: hasSourceTemplateId,
    templateId: sourceTemplateId,
    apiReady,
    channelKey,
  });

  useEffect(() => {
    setEditorContent(initialEditorContent);
  }, [initialEditorContent]);

  // TinyMCE's normalization of the server markup is not a user edit, so the dirty baseline has to
  // be taken against what the editor holds, not the raw server value.
  const onEditorInit = useCallback((normalizedContent) => {
    setEditorContent(normalizedContent);
    setIsEditorReady(true);
  }, []);

  const sourceTemplateKey = `${String(sourceTemplateId ?? '')}-${initialBasicInfoForm ? 'loaded' : 'init'}`;
  const basicInfoCardKey = isEditMode
    ? `edit-${sourceTemplateKey}`
    : isCopyMode
      ? `copy-${sourceTemplateKey}`
      : `create-${channelKey}`;

  const {
    basicInfoForm,
    setBasicInfoForm,
    setBasicInfoTouched,
    basicInfoSaveAttempt,
    revealNonce,
    hasTriedSave,
    discardDialogOpen,
    showSaveOnDiscard,
    isSaveDisabled,
    isDeactivateDisabled,
    isPublishDisabled,
    isAlreadyPublished,
    onLeaveTemplatesClick,
    onDiscardClick,
    onDiscardConfirm,
    onDiscardClose,
    onSaveAndProceed,
    submitTemplate,
    deactivateTemplate,
    tryPublish,
  } = useTemplateForm({
    channelKey,
    isEditMode,
    // A copy saves as a new template, so the source id must not reach the save/validation paths.
    templateId: isEditMode ? templateId : null,
    initialBasicInfoForm,
    isSourceDataLoading,
    editorContent,
    customStyle: initialCustomStyle,
    logoUrl: tenantLogoUrl ?? '',
    checkedDisclosures,
    senderInfo: tenantSender ?? null,
    currentStatus,
    isAdmin,
    ownTenantId,
    isEditorReady,
  });

  const previewTemplate = buildCreatePreviewTemplate({
    basicInfoForm,
    channelKey,
    editorContent,
    customStyle: initialCustomStyle,
    checkedDisclosures,
    logoUrl: tenantLogoUrl,
    senderInfo: tenantSender,
  });

  if (isSourceDataLoading || isMetaLoading) {
    return (
      <main className={clsx(styles.page)} aria-label={pageTitle}>
        <div className={clsx(layoutStyles.pageContentWrap)}>
          <SpinnerWrap ariaLabel="Loading template" />
        </div>
      </main>
    );
  }

  return (
    <main className={clsx(styles.page)} aria-label={pageTitle}>
      <DiscardChangesDialog
        open={discardDialogOpen}
        onClose={onDiscardClose}
        onDiscard={onDiscardConfirm}
        onSave={showSaveOnDiscard ? onSaveAndProceed : undefined}
        saveLabel={isAlreadyPublished ? 'Publish Changes' : 'Save Changes'}
      />
      <TemplatePreviewPanel
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        template={previewTemplate}
      />
      <PageSubheader
        className={clsx(styles.subheader)}
        left={
          <div className={clsx(styles.subheaderLeft)}>
            <IconButton
              ariaLabel="Back to Templates"
              onClick={onLeaveTemplatesClick}
              className={clsx(styles.backButton)}
              size="sm"
            >
              <ChevronLeftIcon className={clsx(styles.backIcon)} aria-hidden />
            </IconButton>
            <h1 className={clsx(styles.title)}>{pageTitle}</h1>
          </div>
        }
        right={
          <CreateTemplateActions
            isEditMode={isEditMode}
            currentStatus={currentStatus}
            isAlreadyPublished={isAlreadyPublished}
            onDiscardClick={onDiscardClick}
            onPreviewOpen={() => setPreviewOpen(true)}
            submitTemplate={submitTemplate}
            deactivateTemplate={deactivateTemplate}
            tryPublish={tryPublish}
            isSaveDisabled={isSaveDisabled}
            isDeactivateDisabled={isDeactivateDisabled}
            isPublishDisabled={isPublishDisabled}
          />
        }
      />

      <div className={clsx(layoutStyles.pageContentWrap)}>
        <div className={clsx(styles.grid)}>
          <div className={clsx(styles.leftCol)}>
            <BasicInformationCard
              key={basicInfoCardKey}
              channel={channelKey}
              saveAttempt={basicInfoSaveAttempt}
              revealNonce={revealNonce}
              onFormChange={setBasicInfoForm}
              onTouchedChange={setBasicInfoTouched}
              initialValues={initialBasicInfoForm}
              requireTemplateName={hasTriedSave}
              hasTriedSave={hasTriedSave}
              currentStatus={isEditMode ? currentStatus : null}
              modified={isEditMode ? modified : null}
              lastEditedBy={isEditMode ? lastEditedBy : null}
              templateId={isEditMode ? templateId : null}
              isEditMode={isEditMode}
              isAdmin={isAdmin}
              ownTenantId={ownTenantId}
            />
            <ContentEditorCard
              key={basicInfoCardKey}
              channel={channelKey}
              initialValue={initialEditorContent}
              customStyle={initialCustomStyle}
              onContentChange={setEditorContent}
              onEditorInit={onEditorInit}
            />
          </div>

          <StickyPanel
            stickyTopVar="--create-template-sticky-top-offset"
            stickyTopFallback={100}
            width="var(--create-template-side-panel-width)"
          >
            <div className={clsx(styles.rightCol)}>
              <div className={clsx(styles.rightScroll, scrollbarStyles.scrollbarThinY)}>
                <VariablesCard channel={variableChannel} />
                {showDisclosure ? (
                  <DisclosureCard
                    disclosures={disclosures}
                    initialCheckedIds={initialCheckedDisclosureIds}
                    onCheckedChange={setCheckedDisclosures}
                  />
                ) : null}
              </div>
            </div>
          </StickyPanel>
        </div>
      </div>
    </main>
  );
}
